import * as Sentry from '@sentry/react'

export function initSentry() {
  // Only initialize Sentry in production
  if (process.env.NODE_ENV === 'production') {
    Sentry.init({
      // TODO: Add your Sentry DSN here
      dsn: process.env.VITE_SENTRY_DSN || '',
      
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask all text content for privacy
          maskAllText: true,
          // Don't record any network request/response bodies
          networkDetailAllowUrls: [],
        }),
      ],
      
      // Performance Monitoring
      tracesSampleRate: 0.1, // 10% of transactions
      
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Release tracking
      release: process.env.VITE_APP_VERSION || 'unknown',
      
      // Environment
      environment: process.env.NODE_ENV,
      
      // Privacy settings
      beforeSend(event, hint) {
        // Don't send events in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Sentry Event:', event, hint)
          return null
        }
        
        // Filter out sensitive data
        if (event.request?.cookies) {
          delete event.request.cookies
        }
        
        // Remove any PII from URLs
        if (event.request?.url) {
          event.request.url = event.request.url.replace(/case\/WB-\d{4}-[A-Z0-9]{5}/g, 'case/[REDACTED]')
        }
        
        return event
      },
      
      // Don't log to console in production
      beforeBreadcrumb(breadcrumb) {
        // Filter out sensitive breadcrumbs
        if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
          return null
        }
        return breadcrumb
      },
      
      // Ignore certain errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        // Random network errors
        'Network request failed',
        'NetworkError',
        'Failed to fetch',
        // Benign errors
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
    })
  }
}

// Helper to capture exceptions with additional context
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (process.env.NODE_ENV === 'development') {
    console.error('Error:', error, context)
  } else {
    Sentry.captureException(error, {
      contexts: {
        custom: context,
      },
    })
  }
}

// Helper to capture messages
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[${level}]`, message)
  } else {
    Sentry.captureMessage(message, level)
  }
}

// Helper to add breadcrumbs
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb)
}

// Helper to set user context (without PII)
export function setUserContext(userId: string, role?: string) {
  Sentry.setUser({
    id: userId,
    // Don't include email or name for privacy
    role,
  })
}

// Helper to clear user context on logout
export function clearUserContext() {
  Sentry.setUser(null)
}